import { useState, useEffect } from 'react';
import {
  Search, Loader2, AlertCircle, X, ArrowUpRight, ArrowDownLeft, CreditCard, Filter
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [fCompany, setFCompany] = useState('');
  const [fCompanyInput, setFCompanyInput] = useState('');
  const [showCompanySuggest, setShowCompanySuggest] = useState(false);
  const [fVendor, setFVendor] = useState('');
  const [fVendorInput, setFVendorInput] = useState('');
  const [showVendorSuggest, setShowVendorSuggest] = useState(false);
  const [fCustomer, setFCustomer] = useState('');
  const [fCustomerInput, setFCustomerInput] = useState('');
  const [showCustomerSuggest, setShowCustomerSuggest] = useState(false);
  const [fPaymentType, setFPaymentType] = useState('');
  const [fPaymentMode, setFPaymentMode] = useState('');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fActivePreset, setFActivePreset] = useState('');
  const [applied, setApplied] = useState({ company: '', vendor: '', customer: '', paymentType: '', paymentMode: '', dateFrom: '', dateTo: '' });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchTransactions(); }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/transactions`, getAuthHeaders());
      setTransactions(res.data);
    } catch (err) { setError('Failed to fetch transactions'); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  // Derived suggestion lists
  const companyNames = [...new Set(transactions.map(t => t.company_name).filter(Boolean))].sort();
  const vendorNames = [...new Set(transactions.map(t => t.vendor_name).filter(Boolean))].sort();
  const customerNames = [...new Set(transactions.map(t => t.customer_name).filter(Boolean))].sort();
  const paymentModes = [...new Set(transactions.map(t => t.payment_type).filter(Boolean))].sort();

  const companySuggestions = fCompanyInput.trim() ? companyNames.filter(n => n.toLowerCase().includes(fCompanyInput.toLowerCase())) : companyNames.slice(0, 8);
  const vendorSuggestions = fVendorInput.trim() ? vendorNames.filter(n => n.toLowerCase().includes(fVendorInput.toLowerCase())) : vendorNames.slice(0, 8);
  const customerSuggestions = fCustomerInput.trim() ? customerNames.filter(n => n.toLowerCase().includes(fCustomerInput.toLowerCase())) : customerNames.slice(0, 8);

  function calcPreset(preset) {
    const now = new Date(); let fd = '', td = '';
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'this_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth(), 1)); td = fmt(now); }
    else if (preset === 'last_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth()-1, 1)); td = fmt(new Date(now.getFullYear(), now.getMonth(), 0)); }
    else if (preset === 'this_quarter') { const qs = Math.floor(now.getMonth()/3)*3; fd = fmt(new Date(now.getFullYear(), qs, 1)); td = fmt(now); }
    else if (preset === 'last_quarter') { const qs = Math.floor(now.getMonth()/3)*3-3; const y = qs<0?now.getFullYear()-1:now.getFullYear(); const qss=((qs%12)+12)%12; fd=fmt(new Date(y,qss,1)); td=fmt(new Date(y,qss+3,0)); }
    else if (preset === 'this_year') { fd = fmt(new Date(now.getFullYear(), 0, 1)); td = fmt(now); }
    else if (preset === 'last_year') { fd = fmt(new Date(now.getFullYear()-1, 0, 1)); td = fmt(new Date(now.getFullYear()-1, 11, 31)); }
    return { fd, td };
  }
  const applyPreset = (preset) => { const { fd, td } = calcPreset(preset); setFActivePreset(preset); setFDateFrom(fd); setFDateTo(td); };

  const applyFilters = () => setApplied({ company: fCompany, vendor: fVendor, customer: fCustomer, paymentType: fPaymentType, paymentMode: fPaymentMode, dateFrom: fDateFrom, dateTo: fDateTo });
  const clearFilters = () => {
    setFCompany(''); setFCompanyInput('');
    setFVendor(''); setFVendorInput('');
    setFCustomer(''); setFCustomerInput('');
    setFPaymentType(''); setFPaymentMode('');
    setFDateFrom(''); setFDateTo(''); setFActivePreset('');
    setApplied({ company: '', vendor: '', customer: '', paymentType: '', paymentMode: '', dateFrom: '', dateTo: '' });
  };
  const hasFilters = applied.company || applied.vendor || applied.customer || applied.paymentType || applied.paymentMode || applied.dateFrom || applied.dateTo;

  const filtered = (() => {
    const q = searchQuery.toLowerCase();
    let r = transactions.filter(t =>
      !q || (t.reference_label || '').toLowerCase().includes(q) ||
      (t.vendor_name || '').toLowerCase().includes(q) ||
      (t.customer_name || '').toLowerCase().includes(q) ||
      (t.payment_type || '').toLowerCase().includes(q) ||
      (t.receipt_number || '').toLowerCase().includes(q) ||
      (t.reference_number || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
    if (applied.company) r = r.filter(t => t.company_name === applied.company);
    if (applied.vendor) r = r.filter(t => t.vendor_name === applied.vendor);
    if (applied.customer) r = r.filter(t => t.customer_name === applied.customer);
    if (applied.paymentType) r = r.filter(t => t.type === applied.paymentType);
    if (applied.paymentMode) r = r.filter(t => t.payment_type === applied.paymentMode);
    if (applied.dateFrom) r = r.filter(t => { const d = (t.date || t.created_at || '').split('T')[0]; return d >= applied.dateFrom; });
    if (applied.dateTo) r = r.filter(t => { const d = (t.date || t.created_at || '').split('T')[0]; return d <= applied.dateTo; });
    return r;
  })();

  const totalOut = filtered.filter(t => t.type === 'payment_out').reduce((s, t) => s + (t.amount || 0), 0);
  const totalIn = filtered.filter(t => t.type === 'payment_in').reduce((s, t) => s + (t.amount || 0), 0);

  const AutoSuggest = ({ value, inputVal, setInputVal, setValue, showSuggest, setShowSuggest, suggestions, placeholder, testId }) => (
    <div className="relative">
      <input type="text" value={inputVal}
        onChange={e => { setInputVal(e.target.value); setValue(''); setShowSuggest(true); }}
        onFocus={() => setShowSuggest(true)}
        onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
        placeholder={placeholder} data-testid={testId}
        className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
      {inputVal && <button onClick={() => { setValue(''); setInputVal(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>}
      {showSuggest && suggestions.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-auto">
          {suggestions.map(name => (
            <button key={name} onMouseDown={() => { setValue(name); setInputVal(name); setShowSuggest(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${value === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="transactions-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Transactions</h1>
        <p className="text-slate-600 mt-1">All payment transactions history</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <ArrowDownLeft className="h-5 w-5 text-green-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Total Received</span>
          </div>
          <span className="text-2xl font-bold text-green-600" data-testid="total-in">₹{totalIn.toLocaleString()}</span>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <ArrowUpRight className="h-5 w-5 text-red-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Total Paid Out</span>
          </div>
          <span className="text-2xl font-bold text-red-600" data-testid="total-out">₹{totalOut.toLocaleString()}</span>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-slate-600">Net</span>
          </div>
          <span className={`text-2xl font-bold ${(totalIn - totalOut) >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="total-net">
            ₹{(totalIn - totalOut).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by receipt, reference, vendor, customer, payment mode..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>
          {hasFilters && <button onClick={clearFilters} data-testid="clear-filters-btn" className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label>
            <AutoSuggest value={fCompany} inputVal={fCompanyInput} setInputVal={setFCompanyInput} setValue={setFCompany}
              showSuggest={showCompanySuggest} setShowSuggest={setShowCompanySuggest}
              suggestions={companySuggestions} placeholder="Type company..." testId="filter-company-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Vendor</label>
            <AutoSuggest value={fVendor} inputVal={fVendorInput} setInputVal={setFVendorInput} setValue={setFVendor}
              showSuggest={showVendorSuggest} setShowSuggest={setShowVendorSuggest}
              suggestions={vendorSuggestions} placeholder="Type vendor..." testId="filter-vendor-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Customer</label>
            <AutoSuggest value={fCustomer} inputVal={fCustomerInput} setInputVal={setFCustomerInput} setValue={setFCustomer}
              showSuggest={showCustomerSuggest} setShowSuggest={setShowCustomerSuggest}
              suggestions={customerSuggestions} placeholder="Type customer..." testId="filter-customer-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Payment Type</label>
            <select value={fPaymentType} onChange={e => setFPaymentType(e.target.value)} data-testid="filter-payment-type"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
              <option value="">All</option>
              <option value="payment_in">Payment In</option>
              <option value="payment_out">Payment Out</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Payment Mode</label>
            <select value={fPaymentMode} onChange={e => setFPaymentMode(e.target.value)} data-testid="filter-payment-mode"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
              <option value="">All</option>
              {paymentModes.map(pm => <option key={pm} value={pm}>{pm}</option>)}
            </select>
          </div>
          <div>
            <button onClick={applyFilters} data-testid="apply-filters-btn" className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">Apply</button>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Date</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {[{ key: 'this_month', label: 'This Month' }, { key: 'last_month', label: 'Last Month' }, { key: 'this_quarter', label: 'This Quarter' }, { key: 'last_quarter', label: 'Last Quarter' }, { key: 'this_year', label: 'This Year' }, { key: 'last_year', label: 'Last Year' }].map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${fActivePreset === p.key ? 'bg-orange-500 text-white border-orange-500' : 'border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="h-5 w-px bg-slate-200 hidden md:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label>
                <input type="date" value={fDateFrom} onChange={e => { setFDateFrom(e.target.value); setFActivePreset(''); }} data-testid="date-from" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-slate-500 font-medium">To</label>
                <input type="date" value={fDateTo} onChange={e => { setFDateTo(e.target.value); setFActivePreset(''); }} data-testid="date-to" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <CreditCard className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No transactions found</h3>
            <p className="text-slate-500">Payment transactions will appear here once recorded via Payment In or Payment Out</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-9 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Type</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Reference</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Party</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Receipt / Ref.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Payment Mode</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Amount</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Description</div>
            </div>
            {filtered.map((txn) => (
              <div key={txn.id} data-testid={`txn-row-${txn.id}`}
                className="grid grid-cols-1 md:grid-cols-9 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="text-slate-600 text-sm">{txn.date ? formatDate(txn.date) : formatDateTime(txn.created_at)}</div>
                <div>
                  {txn.type === 'payment_in' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                      <ArrowDownLeft className="h-3 w-3" /> In
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                      <ArrowUpRight className="h-3 w-3" /> Out
                    </span>
                  )}
                </div>
                <div className="font-mono text-sm font-medium text-slate-900">{txn.reference_label || '-'}</div>
                <div className="text-slate-700 text-sm">{txn.vendor_name || txn.customer_name || '-'}</div>
                <div className="text-sm font-mono">
                  <div className="font-semibold text-slate-800">{txn.receipt_number || '-'}</div>
                  {txn.reference_number && <div className="mt-1 text-xs text-slate-500">Ref: {txn.reference_number}</div>}
                </div>
                <div>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {txn.payment_type}
                  </span>
                </div>
                <div className={`font-medium ${txn.type === 'payment_in' ? 'text-green-600' : 'text-red-600'}`}>
                  {txn.type === 'payment_in' ? '+' : '-'} ₹{txn.amount?.toLocaleString()}
                </div>
                <div className="text-slate-500 text-sm truncate col-span-2">{txn.description || txn.notes || '-'}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
